import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StyledDatePicker } from 'components/molecules/FormDatePicker/styles';
import { getDateFromISOOrNull, isValidDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { DateView } from '@mui/x-date-pickers';
import { getNestedErrorMessage } from '../FormInput/helpers';
import { colors } from 'theme/colors';

export enum EFormInputTypes {
  text = 'text',
  password = 'password',
}

interface IFormInput extends IFormElementProps {
  placeholder?: string;
  minDate?: Date;
  openTo?: DateView;
  arrayName?: string;
  arrayIndex?: number;
  fieldName?: string;
  dateFormat?: string;
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
}

export const FormDatePicker = ({
  name,
  label,
  control,
  errors,
  minDate,
  openTo = 'day',
  arrayName,
  arrayIndex,
  fieldName,
  placeholder,
  dateFormat,
  disabled = false,
  helperText,
  required = false,
}: IFormInput) => {
  const [error, setError] = useState<string>('');
  const { shortDateFormat, getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    let isError = false;
    if (name.includes('.') && !name.includes('[') && Object.keys(errors).length) {
      const keys = name.split('.');
      const errorMessage = getNestedErrorMessage(errors, keys);
      if (keys && errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    } else {
      const errorMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
          : errors[name]?.message;

      isError = true;
      setError(errorMessage);
    }
    if (!isError) {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  const finalDateFormat = dateFormat || shortDateFormat;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledFormElementWrapper disabled={disabled}>
        <FormLabel name={name} label={label} disabled={disabled} required={required} />
        <Controller
          name={name}
          control={control}
          defaultValue={null}
          render={({ field }) => (
            <StyledDatePicker
              openTo={openTo}
              onAccept={(newDate) => {
                if (newDate === null) {
                  field.onChange(null);
                }
              }}
              disabled={disabled}
              format={finalDateFormat}
              value={getDateFromISOOrNull(field.value)}
              minDate={minDate}
              onChange={(e) => {
                if (isValidDate(e) || e === null) {
                  field.onChange(e);
                }
              }}
              slotProps={{
                field: {
                  clearable: true,
                },
                textField: {
                  ref: field.ref,
                  sx: {
                    fieldset: {
                      borderRadius: 4,
                    },
                    input: {
                      fontSize: 14,
                      height: 32,
                      backgroundColor: colors.white,
                      boxSizing: 'border-box',
                    },
                    '& .MuiSelect-select': {
                      height: '32px',
                      boxSizing: 'border-box',
                      backgroundColor: colors.white,
                    },
                    '& .MuiPickersOutlinedInput-notchedOutline': {
                      borderRadius: '4px',
                    },
                    '& .MuiPickersOutlinedInput-root': {
                      width: '100%',
                      height: '32px',
                      fontSize: '14px',
                      fontWeight: 600,
                      backgroundColor: colors.white,
                      '& fieldset': {
                        borderColor: colors.gray400,
                        borderWidth: '1px',
                      },
                      '&:hover:not(.Mui-focused, .Mui-disabled) fieldset': {
                        borderColor: colors.primary400,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary400,
                        borderWidth: '2px',
                      },
                      '&:active fieldset': {
                        borderColor: colors.primary900,
                        borderWidth: '1px',
                      },
                      '&.Mui-error fieldset': {
                        borderColor: colors.error800,
                        borderWidth: '1px',
                      },
                      '& .MuiInputAdornment-root .MuiIconButton-root': {
                        color: colors.gray900,
                      },
                      '&.Mui-disabled .MuiInputAdornment-root .MuiIconButton-root': {
                        color: colors.gray400,
                      },
                    },
                  },
                  slotProps: {
                    htmlInput: {
                      inputMode: 'numeric',
                      placeholder: placeholder || finalDateFormat.toUpperCase(),
                      id: name,
                      disabled: !!disabled,
                    },
                  },
                  error: !!error,
                  helperText,
                },
              }}
            />
          )}
        />
        <FormErrorLabel label={error} />
      </StyledFormElementWrapper>
    </LocalizationProvider>
  );
};
